// player-application/src/store.js

import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, REHYDRATE } from 'redux-persist';

import setlistReducer from './features/setlist/setlistSlice';
import setlistsReducer from './features/setlists/setlistsSlice';
import sessionReducer from './features/session/sessionSlice';
import authReducer from './features/auth/authSlice';
import libraryReducer from './features/library/librarySlice';
import lyricsReducer from './features/lyrics/lyricsSlice';
import uiReducer from './features/ui/uiSlice';
import offlineReducer from './features/offline/offlineSlice';

import localforage from 'localforage';
import localStorageStorage from 'redux-persist/lib/storage'; // localStorage adapter

// pgw 30-sep-2025 - replace all debugging debugLog calls with debugLog
import { debugLog } from './debugLog';

import {
  cleanupLegacyLocalStorage,
  cleanupLegacyIndexedDB,
} from './persist/migrateBothWays';

import { logToIndexedDB } from './indexedLogger';

export const DEBUG_LOG_KEY = 'debugLogs';

/* --------------------------- local logger (unchanged) --------------------------- */
export const logToLocalStorage = (message, payload = null) => {
  const logs = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY)) || [];

  const now = new Date();
  const ukTimeWithSeconds = now.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'Europe/London', hour12: false,
  });

  let msg = `${ukTimeWithSeconds}`;
  if (payload !== null && payload !== undefined) {
    let safe; try { safe = JSON.stringify(payload); } catch { safe = String(payload); }
    msg = `${msg} m=${message} p=${safe}`;
  } else {
    msg = `${msg} m=${message}`;
  }
  logs.push(msg);
  // console.log(' 16-oct message = ', message);
  localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(logs));
};

/* --------------------------- localforage config (IDB) --------------------------- */
localforage.config({
  name: 'ChoirPlayer',
  storeName: 'persistRoot',
  description: 'Redux Persist storage in IndexedDB',
});

/* --------------------------- choose backend (flag) --------------------------- */
function toBool(v) {
  if (typeof v === 'boolean') return v;
  if (v == null) return undefined;
  const s = String(v).trim().toLowerCase();
  if (['1','true','yes','on'].includes(s)) return true;
  if (['0','false','no','off'].includes(s)) return false;
  return undefined;
}

// 1) preference set by runtime flipper (PREF HAS PRIORITY)
let PREF;
if (typeof window !== 'undefined') {
  PREF = window.localStorage.getItem('__persist:prefer_ls');
  // console.log(' 16-oct [persist] PREF __persist:prefer_ls =', PREF);
} else {
  PREF = undefined;
  // console.log(' 16-oct [persist] PREF skipped (window is undefined)');
}

// 2) optional runtime flag from public/env.js
let runtime;
if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
  runtime = window.__RUNTIME_CONFIG__;
  // console.log(' 16-oct [persist] runtime.USE_LS =', runtime.USE_LS);
} else {
  runtime = {};
  // console.log(' 16-oct [persist] runtime.__RUNTIME_CONFIG__ not defined');
}

// 3) CRA build-time flag
let CRA;
if (typeof process !== 'undefined' && process.env) {
  CRA = process.env.REACT_APP_USE_LS;
  // console.log(' 16-oct [persist] CRA REACT_APP_USE_LS =', CRA);
} else {
  CRA = undefined;
  // console.log(' 16-oct [persist] CRA env not available');
}

// FINAL DECISION ORDER: PREF → runtime → CRA → default(false = IDB)
let USE_LS;
const prefBool = toBool(PREF);
if (prefBool !== undefined) {
  USE_LS = prefBool;
  // console.log(' 16-oct [persist] Decision: using PREF (USE_LS =', USE_LS, ')');
} else {
  const runtimeBool = toBool(runtime.USE_LS);
  if (runtimeBool !== undefined) {
    USE_LS = runtimeBool;
    // console.log(' 16-oct [persist] Decision: using runtime.USE_LS (USE_LS =', USE_LS, ')');
  } else {
    const craBool = toBool(CRA);
    if (craBool !== undefined) {
      USE_LS = craBool;
      // console.log(' 16-oct [persist] Decision: using CRA env (USE_LS =', USE_LS, ')');
    } else {
      USE_LS = false;
      // console.log(' 16-oct [persist] Decision: defaulting to IndexedDB (USE_LS = false)');
    }
  }
}
// console.log(' 16-oct [persist] Final USE_LS =', USE_LS ? 'localStorage' : 'indexedDB');

/* ---------------------- preflight migration (blocking for hydrate) ---------------------- */
const PERSIST_KEY = 'persist:root';

async function idbGetRaw() { await localforage.ready(); return localforage.getItem(PERSIST_KEY); }
async function idbSetRaw(raw) { await localforage.ready(); return localforage.setItem(PERSIST_KEY, raw); }
function lsGetRaw() { try { return window.localStorage.getItem(PERSIST_KEY); } catch { return null; } }
function lsSetRaw(raw) { window.localStorage.setItem(PERSIST_KEY, raw); }

async function preflightCopyLsToIdb() {
  if (typeof window === 'undefined') return false;
  const lsBlob = lsGetRaw();
  if (lsBlob == null) { /* console.log(' 16-oct [preflight] LS→IDB: no LS blob — skip'); */ return false; }
  const idbBlob = await idbGetRaw();
  if (idbBlob != null) { /* console.log(' 16-oct [preflight] LS→IDB: IDB already has blob — skip'); */ return false; }
  await idbSetRaw(lsBlob);
//   console.log(' 16-oct [preflight] LS→IDB: copied raw persist:root');
  return true;
}

async function preflightCopyIdbToLs() {
  if (typeof window === 'undefined') return false;
  const idbBlob = await idbGetRaw();
  if (idbBlob == null) {  /* console.log(' 16-oct [preflight] IDB→LS: no IDB blob — skip'); */ return false; }
  const lsBlob = lsGetRaw();
  if (lsBlob != null) {  /* console.log(' 16-oct [preflight] IDB→LS: LS already has blob — skip'); */  return false; }
  lsSetRaw(idbBlob);
//   console.log(' 16-oct [preflight] IDB→LS: copied raw persist:root');
  return true;
}

const preflightPromise = (async () => {
  try {
    if (USE_LS) {
      // Hydrate from LS → ensure LS has blob (maybe copy from IDB)
      return await preflightCopyIdbToLs();
    } else {
      // Hydrate from IDB → ensure IDB has blob (maybe copy from LS)
      return await preflightCopyLsToIdb();
    }
  } catch (e) {
    console.warn('[preflight] error:', e);
    return false;
  }
})();

/* ---------------------- gated storage (delays hydrate until preflight) ---------------------- */
function makeGatedStorage(baseStorage) {
  return {
    getItem: (key) => preflightPromise.then(() => baseStorage.getItem(key)),
    setItem: (key, value) => baseStorage.setItem(key, value),
    removeItem: (key) => baseStorage.removeItem(key),
  };
}

/* ---------------------- pick storage + post-hydrate cleanup ---------------------- */
const DEFAULT_KEYS = { lsKey: PERSIST_KEY, lfKey: PERSIST_KEY };

let persistConfig;
let afterHydrateCleanup = () => {};

if (USE_LS) {
  // console.log(' 16-oct [persist] Target backend: localStorage');
  const gated = makeGatedStorage(localStorageStorage);
  persistConfig = { key: 'root', storage: gated, debug: true, timeout: 0 };
  afterHydrateCleanup = () => { void cleanupLegacyIndexedDB({ lfKey: DEFAULT_KEYS.lfKey }); };
} else {
  // console.log(' 16-oct [persist] Target backend: IndexedDB via localforage');
  const gated = makeGatedStorage(localforage);
  persistConfig = { key: 'root', storage: gated, debug: true, timeout: 0 };
  afterHydrateCleanup = () => { cleanupLegacyLocalStorage({ lsKey: DEFAULT_KEYS.lsKey }); };
}

/* ------------------------------- reducers ------------------------------- */
const combinedReducer = combineReducers({
  setlist: setlistReducer,
  session: sessionReducer,
  auth: authReducer,
  library: libraryReducer,
  ui: uiReducer,
  offline: offlineReducer,
  setlists: setlistsReducer,
  lyrics: lyricsReducer,
});

const persistedReducer = persistReducer(persistConfig, combinedReducer);

/* ------------------------------- store ------------------------------- */
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefault) =>
    getDefault({ serializableCheck: { ignoredActions: [REHYDRATE] } }),
});

/* ------------------------------- persistor ------------------------------- */
export const persistor = persistStore(store, null, () => {
  try { afterHydrateCleanup(); } catch {}
});

// Expose for debugging
if (typeof window !== 'undefined') {
  window.__STORE_BACKEND__ = USE_LS ? 'localStorage' : 'indexedDB';
  window.__PERSISTOR__ = persistor;
}

/* ---------------------- optional logging on bootstrap ---------------------- */
function logAllSetlists(state) {

	// pgw 17-oct-2025
	return;

  try {
    const setlists = state?.setlists?.setlists;
    logToIndexedDB('store: Current setlists len=', Array.isArray(setlists) ? setlists.length : 0);
    if (Array.isArray(setlists)) {
      setlists.forEach((sl) => {
        logToIndexedDB('sl.title = ', sl?.title);
        sl?.items?.forEach((song) => logToIndexedDB('song.mix = ', song?.mix));
      });
    }
  } catch (e) {
    logToIndexedDB('store logAllSetlists, e=', e);
  }
}

export default store;

persistor.subscribe(() => {
  if (persistor.getState().bootstrapped) {
    logToIndexedDB('store - Redux rehydration complete');
    const state = store.getState();
    logToIndexedDB('store - before calling logAllSetlists');
    logAllSetlists(state);
  } else {
    logToIndexedDB('store - Redux rehydration not complete');
  }
});